import { action, computed, observable, makeObservable } from 'mobx'
import { task } from 'mobx-task'
import once from 'lodash/once'
import { CancellationToken } from 'infra/cancellation'
import ModalViewModel, {
  ModalViewModelClosingSignal,
} from 'infra/ModalViewModel'
import { whileActive } from 'utils/rx-util'
import { toStream } from 'utils/mobx-util'
import Job from 'jobs/Job'

export default class RenameJobViewModel extends ModalViewModel {
  @observable newName = ''

  constructor(
    public job: Job,
    private _rename: (job: Job, newName: string) => Promise<unknown>,
    closingSignal?: ModalViewModelClosingSignal
  ) {
    super(closingSignal)
    makeObservable(this)
    // When the job name changes (realtime updates), update the draft name.
    toStream(() => this.job.name || this.job.legendName, true)
      .pipe(whileActive(this.showing$))
      .subscribe(this.setName)
  }

  /**
   * Whether the name has been changed.
   */
  @computed
  get nameHasChanged() {
    return this.job.name !== this.newName.trim()
  }

  /**
   * Shows the dialog and inits the name field.
   */
  show() {
    if (this.showing) {
      return
    }

    super.show()
  }

  /**
   * Sets the name.
   *
   * @param name
   */
  @action.bound
  setName(name: string) {
    this.newName = name
  }

  /**
   * Rename the job.
   *
   * @param newName
   */
  rename = task.resolved(async () => {
    await this._rename(this.job, this.newName.trim())
    this.close()
  })

  /**
   * A default instance.
   */
  static get none() {
    return lazyNone()
  }
}

const lazyNone = once(
  () =>
    new RenameJobViewModel(
      Job.none,
      () => Promise.resolve(),
      CancellationToken.cancelled
    )
)
